import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  checkHardwareRequestTicket,
  linkHardwareRequestTicket,
  unlinkHardwareRequestTicket,
} from '@/api/hardwareRequests';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { formatDateTime } from '@/lib/dateFormat';
import type { HardwareRequestDetail, HardwareRequestTicketCheck } from '@/types/oscar';

/**
 * Pointing a request's decisions at a different Zendesk ticket (093).
 *
 * Anthony, 07/10/2026: *"need to be able to link to another ticket. the linked
 * ticket was mistakenly closed."*
 *
 * ⛔ A CLOSED ZENDESK TICKET CANNOT BE REOPENED. Once somebody closes a
 * request's ticket by hand, every write-back answers 422 "closed prevents
 * ticket update" and Try again retries forever against a wall. This is the only
 * way out of that state.
 *
 * ⛔ CHECK BEFORE LINK, and the Link button stays disabled until a check has
 * come back usable. Linking a second closed ticket would record a redirection
 * that cannot work — the same dead end, one ticket along.
 *
 * ⚠ The ORIGINAL ticket is never replaced on screen. Both are shown: where the
 * request was raised, and where its decisions now go.
 */
export function TicketLinkControl({ request }: { request: HardwareRequestDetail }) {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [ticket, setTicket] = useState('');
  const [reason, setReason] = useState('');
  const [check, setCheck] = useState<HardwareRequestTicketCheck | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['hardware-request', request.SubmissionId] });
    void queryClient.invalidateQueries({ queryKey: ['hardware-requests'] });
  };

  const typed = Number(ticket.trim());
  const typedIsValid = Number.isInteger(typed) && typed > 0;

  // ⛔ The check is tied to the number it was made against. Typing a new number
  // after a good check must NOT leave the old verdict enabling the button.
  const checkedThis = check !== null && check.ZendeskTicketId === typed;
  const usable = checkedThis && check.Exists && check.CanBeWrittenTo;

  const checking = useMutation({
    mutationFn: () => checkHardwareRequestTicket(request.SubmissionId, typed),
    onSuccess: (result) => {
      setCheck(result);
      setError(null);
    },
    onError: (e: unknown) => {
      setCheck(null);
      setError(e instanceof ApiError ? e.message : 'The ticket could not be checked.');
    },
  });

  const linking = useMutation({
    mutationFn: () => linkHardwareRequestTicket(request.SubmissionId, typed, reason.trim()),
    onSuccess: () => {
      setOpen(false);
      setTicket('');
      setReason('');
      setCheck(null);
      setError(null);
      refresh();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'The ticket could not be linked.');
    },
  });

  const unlinking = useMutation({
    mutationFn: () => unlinkHardwareRequestTicket(request.SubmissionId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'The link could not be withdrawn.');
    },
  });

  // ⚠ A RENDERING NICETY, NOT THE GATE. It only avoids an empty wrapper for a
  // non-admin looking at a request nobody has redirected; the `isAdmin &&`
  // guards below are what actually withhold the controls, and they are what
  // the tests pin. A negative control proved this line carries no rule of its
  // own — removing it fails nothing — so do not read it as the boundary.
  if (!isAdmin && !request.IsRedirected) return null;

  return (
    <div className="mt-2">
      {request.IsRedirected && (
        <div className="rounded border border-border bg-bg px-3 py-2 text-xs text-darkGrey">
          <div>
            <strong>Decisions for this request go to ticket #{request.LinkedTicketId}.</strong>{' '}
            {request.ZendeskTicketId
              ? `It was raised on #${request.ZendeskTicketId}, which is not being updated.`
              : 'It was not raised on a ticket.'}
          </div>
          {request.LinkReason && <div className="mt-1">Reason: {request.LinkReason}</div>}
          <div className="mt-1 text-darkGrey/70">
            Linked by {request.LinkedBy ?? 'unknown'}
            {request.LinkedAtUtc ? ` · ${formatDateTime(request.LinkedAtUtc)}` : ''}
          </div>
          {isAdmin && (
            <button
              type="button"
              onClick={() => unlinking.mutate()}
              disabled={unlinking.isPending}
              className="mt-1 font-semibold text-navy underline disabled:opacity-50"
            >
              {unlinking.isPending ? 'Withdrawing…' : 'Withdraw the link'}
            </button>
          )}
        </div>
      )}

      {isAdmin && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-1 font-semibold text-navy underline"
        >
          {request.IsRedirected ? 'Link a different ticket' : 'Send decisions to another ticket'}
        </button>
      )}

      {isAdmin && open && (
        <div className="mt-2 rounded border border-border bg-bg px-3 py-2 text-xs">
          <p className="text-darkGrey">
            A closed Zendesk ticket cannot be reopened, so a decision can never reach one. Point
            this request at a ticket that is still open — the original stays on record.
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <label className="font-semibold text-darkGrey" htmlFor="ticket-link-number">
              Ticket number
            </label>
            <input
              id="ticket-link-number"
              inputMode="numeric"
              value={ticket}
              onChange={(e) => {
                setTicket(e.target.value);
                // ⛔ A new number invalidates the old verdict. Without this the
                // button stays enabled on a check of a different ticket.
                setCheck(null);
                setError(null);
              }}
              className="w-32 rounded border border-border px-2 py-1"
            />
            <button
              type="button"
              onClick={() => checking.mutate()}
              disabled={!typedIsValid || checking.isPending}
              className="rounded border border-navy px-2 py-1 font-semibold text-navy disabled:opacity-50"
            >
              {checking.isPending ? 'Checking…' : 'Check it'}
            </button>
          </div>

          {checkedThis && (
            <p className={`mt-2 ${usable ? 'text-darkGrey' : 'text-orange'}`}>
              {usable ? (
                <>
                  Ticket #{check.ZendeskTicketId} is <strong>{check.Status}</strong>
                  {check.Subject ? ` — ${check.Subject}` : ''}. It can take the decision.
                </>
              ) : (
                check.Error ?? 'That ticket cannot be used.'
              )}
            </p>
          )}

          <div className="mt-2">
            <label className="font-semibold text-darkGrey" htmlFor="ticket-link-reason">
              Why
            </label>
            <input
              id="ticket-link-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={400}
              placeholder="e.g. the original ticket was closed by mistake"
              className="mt-1 w-full rounded border border-border px-2 py-1"
            />
          </div>

          {error && <p className="mt-2 text-orange">{error}</p>}

          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => linking.mutate()}
              // ⛔ Needs a usable CHECK, not merely a number. The server refuses
              // a closed ticket too — this stops somebody getting that far.
              disabled={!usable || reason.trim().length === 0 || linking.isPending}
              className="rounded bg-navy px-3 py-1 font-semibold text-white disabled:opacity-50"
            >
              {linking.isPending ? 'Linking…' : 'Link this ticket'}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setCheck(null);
                setError(null);
              }}
              className="rounded border border-border px-3 py-1 font-semibold text-darkGrey"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
